/**
 * DateRangeInput wraps DateRangePicker from React-dates and gives a list of all default props we use.
 * Styles for DateRangePicker can be found from 'public/reactDates.css'.
 *
 * N.B. *isOutsideRange* in defaultProps is defining what dates are available to booking.
 */
import React, { Component } from 'react';
import { bool, func, instanceOf, oneOf, shape, string, arrayOf } from 'prop-types';
import {
  DateRangePicker,
  isInclusivelyAfterDay,
  isInclusivelyBeforeDay,
  SingleDatePicker,
} from 'react-dates';
import { intlShape, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import moment from 'moment';
import { START_DATE, END_DATE } from '../../util/dates';
import { LINE_ITEM_DAY, propTypes } from '../../util/types';
import config from '../../config';
import {
  isDayBlockedFn,
  isOutsideRangeFn,
  isBlockedBetween,
  apiEndDateToPickerDate,
  pickerEndDateToApiDate,
} from './DateRangeInput.helpers';
import calendarIcon from '../../components/LocationAutocompleteInput/calendarIcon.svg';
import Grid from '@material-ui/core/Grid';
import { IconArrowHead, FieldSelect } from '../../components';
import css from './DateRangeInput.module.css';
import Select from 'react-select';
import 'moment/locale/fr';
import calendarPic from './Calendar.png';
import hourPic from './Horloge.png';
export const HORIZONTAL_ORIENTATION = 'horizontal';
export const ANCHOR_LEFT = 'left';

// Since final-form tracks the onBlur event for marking the field as
// touched (which triggers possible error validation rendering), only
// trigger the event asynchronously when no other input within this
// component has received focus.
//
// This prevents showing the validation error when the user selects a
// value and moves on to another input within this component.
const BLUR_TIMEOUT = 100;

// IconArrowHead component might not be defined if exposed directly to the file.
// This component is called before IconArrowHead component in components/index.js
const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

// Possible configuration options of React-dates
const defaultProps = {
  initialDates: null, // Possible initial date passed for the component
  value: null, // Value should keep track of selected date.

  // input related props
  startDateId: 'startDate',
  endDateId: 'endDate',
  startDatePlaceholderText: null, // Handled inside component
  endDatePlaceholderText: null, // Handled inside component
  disabled: false,
  required: false,
  readOnly: false,
  screenReaderInputMessage: null, // Handled inside component
  showClearDates: false,
  showDefaultInputIcon: false,
  customArrowIcon: <span />,
  customInputIcon: null,
  customCloseIcon: null,
  noBorder: true,
  block: false,

  // calendar presentation and interaction related props
  renderMonthText: null,
  orientation: HORIZONTAL_ORIENTATION,
  anchorDirection: ANCHOR_LEFT,
  horizontalMargin: 0,
  withPortal: false,
  withFullScreenPortal: false,
  appendToBody: false,
  disableScroll: false,
  daySize: 38,
  isRTL: false,
  initialVisibleMonth: null,
  firstDayOfWeek: config.i18n.firstDayOfWeek,
  numberOfMonths: 1,
  keepOpenOnDateSelect: false,
  reopenPickerOnClearDates: false,
  renderCalendarInfo: null,
  hideKeyboardShortcutsPanel: true,

  // navigation related props
  navPrev: <PrevIcon />,
  navNext: <NextIcon />,
  onPrevMonthClick() {},
  onNextMonthClick() {},
  transitionDuration: 200, // milliseconds between next month changes etc.

  renderCalendarDay: undefined, // If undefined, renders react-dates/lib/components/CalendarDay
  // day presentation and interaction related props
  renderDayContents: day => {
    return <span className="renderedDay">{day.format('D')}</span>;
  },
  minimumNights: 1,
  enableOutsideDays: false,
  isDayBlocked: () => false,

  // outside range -><- today ... today+available days -1 -><- outside range
  isOutsideRange: day => {
    const endOfRange = config.dayCountAvailableForBooking - 1;
    return (
      !isInclusivelyAfterDay(day, moment()) ||
      !isInclusivelyBeforeDay(day, moment().add(endOfRange, 'days'))
    );
  },
  isDayHighlighted: () => {},

  // Internationalization props
  // Multilocale support can be achieved with displayFormat like moment.localeData.longDateFormat('L')
  // https://momentjs.com/
  displayFormat: 'ddd, MMM D',
  monthFormat: 'MMMM YYYY',
  weekDayFormat: 'dd',
  phrases: {
    closeDatePicker: null, // Handled inside component
    clearDate: null, // Handled inside component
  },
};
//________________________________________________________________________________________________
class DateRangeInputComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      focusedInput: null,
      currentStartDate: null,
      dateStart: null,
      dateEnd: null,
      focusedEnd: false,
      focusedStart: false,
      startArrivalTime: 'morning',
      endArrivalTime: 'morning',
      focusedEnd1: false,
      focusedStart1: false,
      warning: false,
    };

    this.blurTimeoutId = null;
    this.onDatesChange = this.onDatesChange.bind(this);
    this.onFocusChange = this.onFocusChange.bind(this);
    //________
    this.onEndDateChange = this.onEndDateChange.bind(this);
    this.onStartDateChange = this.onStartDateChange.bind(this);

    this.onChangeStartArrivalTime = this.onChangeStartArrivalTime.bind(this);
    this.onChangeEndArrivalTime = this.onChangeEndArrivalTime.bind(this);
    this.handleWarning = this.handleWarning.bind(this);
  }

  componentDidUpdate(prevProps) {
    // Update focusedInput in case a new value for it is
    // passed in the props. This may occur if the focus
    // is manually set to the date picker.
    if (this.props.focusedInput && this.props.focusedInput !== prevProps.focusedInput) {
      this.setState({ focusedInput: this.props.focusedInput });
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.blurTimeoutId);
  }

  handleWarning() {
    const datesAreNotSelected = !this.state.dateStart || !this.state.dateEnd;
    this.setState(() => ({
      warning: datesAreNotSelected,
    }));
  }

  onChangeStartArrivalTime(e) {
    const startTime = e.value;
    this.setState(() => ({
      startArrivalTime: startTime,
    }));

    const startDate = this.state.dateStart;
    const endDate = this.state.dateEnd;
    const selectedEndTime = this.state.endArrivalTime;

    const startDateToDate = startDate instanceof moment ? startDate.toDate() : null;
    const endDateToDate = endDate instanceof moment ? endDate.toDate() : null;
    //  endDateToDate.setDate(endDateToDate.getDate() + 1);

    this.props.onChange({
      startDate: startDateToDate,
      endDate: endDateToDate,
      startTime: startTime,
      endTime: selectedEndTime,
    });
  }

  onChangeEndArrivalTime(e) {
    const endTime = e.value;
    this.setState(() => ({
      endArrivalTime: endTime,
    }));

    const startDate = this.state.dateStart;
    const endDate = this.state.dateEnd;
    const selectedStartTime = this.state.startArrivalTime;

    const startDateToDate = startDate instanceof moment ? startDate.toDate() : null;
    const endDateToDate = endDate instanceof moment ? endDate.toDate() : null;
    //  endDateToDate.setDate(endDateToDate.getDate() + 1);

    this.props.onChange({
      startDate: startDateToDate,
      endDate: endDateToDate,
      startTime: selectedStartTime,
      endTime: endTime,
    });
  }

  onDatesChange(dates) {
    const { unitType, timeSlots } = this.props;
    const { startDate, endDate } = dates;

    // both dates are selected, a new start date before the previous start
    // date is selected
    const startDateUpdated =
      timeSlots &&
      startDate &&
      endDate &&
      this.state.currentStartDate &&
      startDate.isBefore(this.state.currentStartDate);

    // clear the end date in case a blocked date can be found
    // between previous start date and new start date
    const clearEndDate = startDateUpdated
      ? isBlockedBetween(timeSlots, startDate, moment(this.state.currentStartDate).add(1, 'days'))
      : false;

    const startDateAsDate = startDate instanceof moment ? startDate.toDate() : null;
    const endDateAsDate = clearEndDate ? null : pickerEndDateToApiDate(unitType, endDate);

    this.setState(() => ({
      currentStartDate: startDateAsDate,
    }));

    this.props.onChange({ startDate: startDateAsDate, endDate: endDateAsDate });
  }

  onFocusChange(focusedInput) {
    // DateRangePicker requires 'onFocusChange' function and 'focusedInput'
    // but Fields of React-Form deals with onFocus & onBlur instead
    this.setState({ focusedInput });

    if (focusedInput) {
      window.clearTimeout(this.blurTimeoutId);
      this.props.onFocus(focusedInput);
    } else {
      window.clearTimeout(this.blurTimeoutId);
      this.blurTimeoutId = window.setTimeout(() => {
        this.props.onBlur();
      }, BLUR_TIMEOUT);
    }
  }

  //__________________________________________________________________________
  onEndDateChange(dateEnd) {
    this.setState({ dateEnd });

    this.setState(() => ({
      warning: false,
    }));

    if (this.state.dateStart) {
      const startDate = this.state.dateStart;
      const endDate = dateEnd;
      const startTime = this.state.startArrivalTime;
      const endTime = this.state.endArrivalTime;

      const startDateToDate = startDate instanceof moment ? startDate.toDate() : null;
      const endDateToDate = endDate instanceof moment ? endDate.toDate() : null;
      //  endDateToDate.setDate(endDateToDate.getDate() + 1);

      this.props.onChange({
        startDate: startDateToDate,
        endDate: endDateToDate,
        startTime: startTime,
        endTime: endTime,
      });
    }
  }

  onStartDateChange(dateStart) {
    this.setState({ dateStart });

    if (this.state.dateEnd) {
      const endDate = this.state.dateEnd;
      const startDate = dateStart;
      const startTime = this.state.startArrivalTime;
      const endTime = this.state.endArrivalTime;
      const startDateToDate = startDate instanceof moment ? startDate.toDate() : null;
      const endDateToDate = endDate instanceof moment ? endDate.toDate() : null;

      this.props.onChange({
        startDate: startDateToDate,
        endDate: endDateToDate,
        startTime: startTime,
        endTime: endTime,
      });
    }
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      className,
      unitType,
      initialDates,
      intl,
      name,
      startDatePlaceholderText,
      endDatePlaceholderText,
      onBlur,
      onChange,
      onFocus,
      phrases,
      screenReaderInputMessage,
      useMobileMargins,
      value,
      children,
      render,
      timeSlots,
      ...datePickerProps
    } = this.props;
    /* eslint-enable no-unused-vars */

    const colourStyles = {
      option: (styles, { data, isDisabled, isFocused, isSelected }) => {
        return {
          ...styles,
          backgroundColor: isFocused ? '#85ADD9' : isSelected ? '#85ADD9' : 'white',
          color: isFocused ? 'white' : isSelected ? 'white' : '#85ADD9',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        };
      },
    };

    const isDaily = unitType === LINE_ITEM_DAY;
    const initialStartMoment = initialDates ? moment(initialDates.startDate) : null;
    const initialEndMoment = initialDates ? moment(initialDates.endDate) : null;
    const startDate =
      value && value.startDate instanceof Date ? moment(value.startDate) : initialStartMoment;

    const endDate =
      apiEndDateToPickerDate(unitType, value ? value.endDate : null) || initialEndMoment;

    const startDateIsSelected = this.state.dateStart;

    let isDayBlocked = isDayBlockedFn(
      timeSlots,
      startDate,
      endDate,
      this.state.focusedInput,
      unitType,
      startDateIsSelected
    );

    let isOutsideRange = isOutsideRangeFn(
      timeSlots,
      startDate,
      endDate,
      this.state.focusedInput,
      unitType,
      startDateIsSelected
    );

    const startDatePlaceholderTxt =
      startDatePlaceholderText ||
      intl.formatMessage({ id: 'FieldDateRangeInput.startDatePlaceholderText' });
    const endDatePlaceholderTxt =
      endDatePlaceholderText ||
      intl.formatMessage({ id: 'FieldDateRangeInput.endDatePlaceholderText' });
    const screenReaderInputText =
      screenReaderInputMessage ||
      intl.formatMessage({ id: 'FieldDateRangeInput.screenReaderInputMessage' });
    const closeDatePickerText = phrases.closeDatePicker
      ? phrases.closeDatePicker
      : intl.formatMessage({ id: 'FieldDateRangeInput.closeDatePicker' });
    const clearDateText = phrases.clearDate
      ? phrases.clearDate
      : intl.formatMessage({ id: 'FieldDateRangeInput.clearDate' });

    const classes = classNames(css.inputRoot, className, {
      [css.withMobileMargins]: useMobileMargins,
    });

    const datesAreNotSelected = !this.state.dateStart || !this.state.dateEnd;
    const isHoursEnabled =
      this.state.dateStart === null || this.state.dateEnd === null ? false : true;
    const endMomentOfDayOptions = [
      { value: 'morning', label: '6h et 12h (le matin)' },
      { value: 'afternoon', label: '12h et 18h (l’ap. midi)' },
      { value: 'evening', label: '18h et 00h (le soir)' },
    ];
    const startMomentOfDayOptions = [
      { value: 'morning', label: '6h et 12h (le matin)' },
      { value: 'afternoon', label: '12h et 18h (l’ap. midi)' },
      { value: 'evening', label: '18h et 00h (le soir)' },
    ];

    return (
      <div className={classes}>
        {/* ____________________________________________________________________________________________________ */}

        <Grid container xs={12} spacing={0}>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <span className={css.textTitle}>
                <strong>Date de début</strong> <img className={css.iconPic} src={calendarPic} />
              </span>
            </Grid>

            <Grid item xs={6}>
              <span className={css.textTitle}>
                <strong>Date de fin</strong> <img className={css.iconPic} src={calendarPic} />
              </span>
            </Grid>
          </Grid>

          <Grid container item xs={12}>
            <Grid item xs={6}>
              <div className={css.timeSelectWrapper1}>
                <SingleDatePicker
                  {...datePickerProps}
                  readOnly
                  noBorder={false}
                  date={this.state.dateStart}
                  onDateChange={dateStart => {
                    this.setState({ focusedStart1: false });
                    this.onStartDateChange(dateStart);
                  }}
                  focused={this.state.focusedStart1}
                  onFocusChange={() => {
                    if (!this.state.focusedStart1) {
                      this.setState({ focusedStart1: true, focusedEnd1: false });
                    }
                  }}
                  id="dateStart"
                  numberOfMonths={1}
                  displayFormat="📅 D MMM" //'ddd, MMM D' if you want {day name, date} format
                  isDayBlocked={isDayBlocked}
                  //isOutsideRange={isOutsideRange}
                  placeholder={'Début'}
                  renderMonthText={month => {
                    const monthNames = [
                      'Janvier',
                      'Février',
                      'Mars',
                      'Avril',
                      'Mai',
                      'Juin',
                      'Juillet',
                      'Août',
                      'Septembre',
                      'Octobre',
                      'Novembre',
                      'Décembre',
                    ];
                    const date = month._d;
                    const monthIndex = date.getMonth();
                    const year = date.getFullYear();
                    return <span>{`${monthNames[monthIndex]} ${year}`}</span>;
                  }}
                  renderDayContents={day => {
                    //Change week day with custom day array
                    day._locale._weekdaysMin = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];
                    // return the actual dates value(like 1,2,3 ...) from the moment object.
                    return <span className={css.calendarDay}>{day.format('D')}</span>;
                  }}
                />
              </div>
            </Grid>

            <Grid item xs={6}>
              <div className={css.timeSelectWrapper2}>
                <SingleDatePicker
                  {...datePickerProps}
                  readOnly
                  noBorder={false}
                  date={this.state.dateEnd}
                  onDateChange={dateEnd => {
                    this.setState({ focusedEnd1: false });
                    this.onEndDateChange(dateEnd);
                  }}
                  focused={this.state.focusedEnd1}
                  onFocusChange={() => {
                    if (!this.state.focusedEnd1) {
                      this.setState({ focusedStart1: false, focusedEnd1: true });
                    }
                  }}
                  id="dateEnd"
                  numberOfMonths={1}
                  displayFormat="📅 D MMM"
                  isDayBlocked={isDayBlocked}
                  isOutsideRange={isOutsideRange}
                  placeholder={'Fin'}
                  renderMonthText={month => {
                    const monthNames = [
                      'Janvier',
                      'Février',
                      'Mars',
                      'Avril',
                      'Mai',
                      'Juin',
                      'Juillet',
                      'Août',
                      'Septembre',
                      'Octobre',
                      'Novembre',
                      'Décembre',
                    ];
                    const date = month._d;
                    const monthIndex = date.getMonth();
                    const year = date.getFullYear();
                    return <span>{`${monthNames[monthIndex]} ${year}`}</span>;
                  }}
                  renderDayContents={day => {
                    //Change week day with custom day array
                    day._locale._weekdaysMin = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];
                    // return the actual dates value(like 1,2,3 ...) from the moment object.
                    return <span className={css.calendarDay}>{day.format('D')}</span>;
                  }}
                />
              </div>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <p></p>
          </Grid>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <span className={css.textTitle}>
                J’arrive entre <img className={css.iconPic} src={hourPic} />
              </span>
            </Grid>

            <Grid item xs={6}>
              <span className={css.textTitle}>
                Je pars entre <img className={css.iconPic} src={hourPic} />
              </span>
            </Grid>
          </Grid>

          <Grid container item xs={12}>
            <Grid item xs={6}>
              <div className={css.timeSelectWrapper3}>
                <Select
                  id="dateStartMomentOfDay"
                  name="dateStartMomentOfDay"
                  onChange={
                    datesAreNotSelected ? this.handleWarning : this.onChangeStartArrivalTime
                  }
                  className={isHoursEnabled ? css.timeSelect1 : css.timeSelectDisabled1}
                  options={startMomentOfDayOptions}
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  defaultValue={{ value: 'morning', label: '6h et 12h (le matin)' }}
                  isSearchable={false}
                  styles={colourStyles}
                />
              </div>
            </Grid>

            <Grid item xs={6}>
              <div className={css.timeSelectWrapper4}>
                <Select
                  id="dateEndMomentOfDay"
                  name="dateEndMomentOfDay"
                  label="Choose an option:"
                  onChange={datesAreNotSelected ? this.handleWarning : this.onChangeEndArrivalTime}
                  className={isHoursEnabled ? css.timeSelect2 : css.timeSelectDisabled2}
                  options={endMomentOfDayOptions}
                  components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                  defaultValue={{ value: 'morning', label: '6h et 12h (le matin)' }}
                  isSearchable={false}
                  styles={colourStyles}
                />
              </div>
            </Grid>
          </Grid>
        </Grid>

        <p className={this.state.warning ? css.showWarning : css.hideWarning}>
          Veuillez d'abord sélectionner les dates
        </p>

        <br />
      </div>
    );
  }
}

DateRangeInputComponent.defaultProps = {
  className: null,
  useMobileMargins: false,
  timeSlots: null,
  ...defaultProps,
};

DateRangeInputComponent.propTypes = {
  className: string,
  startDateId: string,
  endDateId: string,
  unitType: propTypes.bookingUnitType.isRequired,
  focusedInput: oneOf([START_DATE, END_DATE]),
  initialDates: instanceOf(Date),
  intl: intlShape.isRequired,
  name: string.isRequired,
  isOutsideRange: func,
  onChange: func.isRequired,
  onBlur: func.isRequired,
  onFocus: func.isRequired,
  phrases: shape({
    closeDatePicker: string,
    clearDate: string,
  }),
  useMobileMargins: bool,
  startDatePlaceholderText: string,
  endDatePlaceholderText: string,
  screenReaderInputMessage: string,
  value: shape({
    startDate: instanceOf(Date),
    endDate: instanceOf(Date),
  }),
  timeSlots: arrayOf(propTypes.timeSlot),
};

export default injectIntl(DateRangeInputComponent);
